import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { UUID } from "angular2-uuid";
import { Subject } from "rxjs";
import { first } from "rxjs/operators";
import { Person } from "src/app/shared/Models/read";
import { AddNewPersonToBuildingDto } from "src/app/shared/Models/write/AddNewPersonToBuildingDto";
import { UpdateContactDto } from "src/app/shared/Models/write/UpdateContactDto";
import {
  PhoneNumberRegexString,
  PostalcodeRegexString,
  HouseNumberRegexString,
  EmailRegexString,
} from "src/app/shared/regex/regexes";
import { OutsideSurveyService } from "src/app/shared/services/outside-survey.service";
import { ToastService } from "src/app/shared/toast.service";
import { ValidateHouseSuffix } from "src/app/shared/validation/address-validation";

@Component({
  selector: "app-contact-form",
  templateUrl: "./contact-form.component.html",
  styleUrls: ["./contact-form.component.scss"],
})
export class ContactFormComponent implements OnChanges, OnDestroy {
  editContactForm: FormGroup;
  @Input() selectedContact: Person;
  @Input() isSearch: boolean;
  @Input() isAuthCanWrite: boolean;
  @Output() removeContactFromList = new EventEmitter<any>();
  @Output() editContact = new EventEmitter<any>();
  @Output() addNewContactToList = new EventEmitter<any>();

  languageOptions = [
    {
      label: $localize`:@@contact.languages.nl:Dutch`,
      value: "NL",
    },
    {
      label: $localize`:@@contact.languages.fr:French`,
      value: "FR",
    },
  ];
  roleOptions = [
    {
      label: $localize`:@@enum.persontype.syndic:Syndic`,
      value: "Syndic",
    },
    {
      label: $localize`:@@enum.persontype.acpvme:ACP_VME`,
      value: "ACP_VME",
    },
    {
      label: $localize`:@@enum.persontype.concierge:Concierge`,
      value: "Concierge",
    },
    {
      label: $localize`:@@enum.persontype.owner:Owner`,
      value: "Owner",
    },
    {
      label: $localize`:@@enum.persontype.occupant:Occupant`,
      value: "Occupant",
    },
    {
      label: $localize`:@@enum.persontype.presentduringvisit:Present During Visit`,
      value: "Present_During_Visit",
    },
  ];

  constructor(
    private outsideSurveyService: OutsideSurveyService,
    private toastService: ToastService
  ) {
    this.editContactForm = new FormGroup({
      name: new FormControl("", Validators.required),
      personType: new FormControl("", [Validators.required]),
      phone: new FormControl("", [Validators.pattern(PhoneNumberRegexString)]),
      email: new FormControl("", Validators.pattern(EmailRegexString)),
      language: new FormControl("", [
        Validators.required,
        Validators.pattern("^(NL|FR)$"),
      ]),
      address: new FormGroup({
        street: new FormControl("", Validators.required),
        number: new FormControl("", [
          Validators.required,
          Validators.pattern(HouseNumberRegexString),
        ]),
        suffix: new FormControl("", ValidateHouseSuffix),
        postalCode: new FormControl("", [
          Validators.required,
          Validators.pattern(PostalcodeRegexString),
        ]),
        city: new FormControl("", Validators.required),
      }),
    });
  }

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty("selectedContact")) this.setForm();
    if (changes.hasOwnProperty("isAuthCanWrite"))
      if (!this.isAuthCanWrite) this.editContactForm.disable();
      else this.editContactForm.enable();
  }

  onRemoveFromListContact() {
    if (!this.isAuthCanWrite) return;

    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.removeContactFromList.emit(this.selectedContact);
  }

  onSaveContact() {
    if (!this.editContactForm.valid) {
      this.triggerFormValidation();
      return;
    }
    const person = new Person().deserialize(this.editContactForm.value);
    person.id = this.selectedContact.id;

    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.outsideSurveyService
      .updateContact(this.createUpdateContactDto())
      .pipe(first())
      .subscribe((res) => {
        this.selectedContact = person;
        this.editContact.emit(person);
      });
  }

  formIsValid(): boolean {
    if (!this.editContactForm.valid) {
      this.triggerFormValidation();
      return false;
    }

    return true;
  }

  saveNewContactToList(buildingId: string) {
    if (!this.editContactForm.valid) {
      this.triggerFormValidation();
      return;
    }

    const newPersonId = UUID.UUID();
    const person = new Person().deserialize(this.editContactForm.value);
    person.buildingId = buildingId;
    person.id = newPersonId;
    const dto = this.createAddNewPersonToBuildingDto(buildingId, newPersonId);
    this.outsideSurveyService
      .saveNewContactToList(dto)
      .pipe(first())
      .subscribe(
        (res) => {
          this.addNewContactToList.emit(person);
        },
        (err) => {
          this.addNewContactToList.emit(null);
        }
      );
  }
  private triggerFormValidation(): void {
    // trigger touch
    Object.keys(this.editContactForm.controls).forEach((field) => {
      const control = this.editContactForm.get(field);
      if (control instanceof FormGroup)
        Object.keys(control.controls).forEach((field2) => {
          control.get(field2).markAsTouched({ onlySelf: true });
        });
      else control.markAsTouched({ onlySelf: true });
    });
  }

  setForm() {
    this.editContactForm.patchValue({
      name: this.selectedContact?.name,
      personType: this.selectedContact?.personType,
      phone: this.selectedContact?.phone,
      email: this.selectedContact?.email,
      language: this.selectedContact?.language,
      address: {
        street: this.selectedContact?.address?.street,
        number: this.selectedContact?.address?.number,
        suffix: this.selectedContact?.address?.suffix,
        postalCode: this.selectedContact?.address?.postalCode,
        city: this.selectedContact?.address?.city,
      },
    });
  }

  createUpdateContactDto(): UpdateContactDto {
    return new UpdateContactDto().deserialize({
      city: this.editContactForm.get("address").get("city").value,
      email: this.editContactForm.get("email").value,
      language: this.editContactForm.get("language").value,
      name: this.editContactForm.get("name").value,
      number: this.editContactForm.get("address").get("number").value,
      personId: this.selectedContact.id,
      personType: this.editContactForm.get("personType").value,
      phone: this.editContactForm.get("phone").value,
      postalCode: this.editContactForm.get("address").get("postalCode").value,
      street: this.editContactForm.get("address").get("street").value,
      suffix: this.editContactForm.get("address").get("suffix").value,
    });
  }

  createAddNewPersonToBuildingDto(
    buildingId: string,
    newPersonId: string
  ): AddNewPersonToBuildingDto {
    return new AddNewPersonToBuildingDto().deserialize({
      personId: newPersonId,
      city: this.editContactForm.get("address").get("city").value,
      email: this.editContactForm.get("email").value,
      language: this.editContactForm.get("language").value,
      name: this.editContactForm.get("name").value,
      number: this.editContactForm.get("address").get("number").value,
      personType: this.editContactForm.get("personType").value,
      phone: this.editContactForm.get("phone").value,
      postalCode: this.editContactForm.get("address").get("postalCode").value,
      street: this.editContactForm.get("address").get("street").value,
      suffix: this.editContactForm.get("address").get("suffix").value,
      buildingId,
    });
  }
}
